import { useState, useEffect } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { Card } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import { Eye, EyeOff, CheckCircle2, AlertCircle, Key, Info } from "lucide-react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { toast } from "sonner";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { cn } from "@/modern/utils";
import { IntegrationAlert } from "@/integrations";

const CompanyCamIntegrationDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [apiToken, setApiToken] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [hasExistingToken, setHasExistingToken] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Subscribe to store changes
  useSwivuStore({
    storeId: STORE_KEYS.extraBoardIntegrationsStore,
    subscriberFn: ({ record }) => {
      // @ts-expect-error - record.key is dynamically typed
      if (record?.key === "companyCam") {
        loadTokenData();
        return false;
      }
      return false;
    }
  });

  // @ts-expect-error - user.can and user.isSandboxBoard are dynamically typed
  const canEdit = !user?.isDemoUser && !user?.isSandboxBoard && user?.can(GRANTS.grant_one);

  useEffect(() => {
    loadTokenData();
  }, [project]);

  const loadTokenData = () => {
    if (!project) return;

    const companyCamRecord = project.extraBoardIntegrationsStore?.find((r: any) => r.key === "companyCam");
    if (companyCamRecord) {
      setApiToken(companyCamRecord.value || "");
      setHasExistingToken(true);
      setValidationSuccess(true);
    } else {
      setApiToken("");
      setHasExistingToken(false);
      setValidationSuccess(false);
    }
    setValidationError("");
  };

  const validateApiKey = async (apiKey: string, url: string): Promise<string> => {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + apiKey
      }
    });
    const result = await response.text();
    return result;
  };

  const handleTokenChange = (value: string) => {
    setApiToken(value);
    setValidationError("");
    setValidationSuccess(false);
  };

  const handleSaveToken = async () => {
    if (!project) return;

    const trimmedToken = apiToken.trim();

    const companyCamRecord = project.extraBoardIntegrationsStore?.find((r: any) => r.key === "companyCam");

    // Handle token removal
    if (trimmedToken === "" && companyCamRecord) {
      // @ts-expect-error - project.extraBoardIntegrationsStore is dynamically typed
      project.extraBoardIntegrationsStore?.remove(companyCamRecord);
      toast.success("CompanyCam access token removed successfully");
      setHasExistingToken(false);
      setValidationSuccess(false);
      setValidationError("");
      return;
    }

    // Validate token if not blank
    if (trimmedToken !== "") {
      setIsValidating(true);
      setValidationError("");

      try {
        const response = await validateApiKey(trimmedToken, "https://api.companycam.com/v2/company");
        const parsedResponse = JSON.parse(response);

        // Check if validation was successful
        if (typeof parsedResponse.errors === "undefined") {
          // Save or update the token
          if (companyCamRecord) {
            // Only update if value changed
            if (companyCamRecord.value !== trimmedToken) {
              companyCamRecord.value = trimmedToken;
              toast.success("CompanyCam API token updated successfully");
              setValidationSuccess(true);
            }
          } else {
            project.extraBoardIntegrationsStore?.add({
              key: "companyCam",
              value: trimmedToken
            });
            toast.success("CompanyCam API token saved successfully");
            setValidationSuccess(true);
          }
          setHasExistingToken(true);
          setValidationError("");
        } else {
          const errorMessage = "Invalid. Please check your API access token and try again.";
          setValidationError(errorMessage);
          setValidationSuccess(false);
          toast.error(errorMessage);
        }
      } catch (error) {
        const errorMessage = "Failed to validate token. Please check your API access token and try again.";
        setValidationError(errorMessage);
        setValidationSuccess(false);
        toast.error(errorMessage);
        console.error("Token validation error:", error);
      } finally {
        setIsValidating(false);
      }
    }
  };

  const handleRemoveToken = async () => {
    // Clear the local state first
    setApiToken("");
    setValidationSuccess(false);
    setValidationError("");

    // Remove from store if exists
    if (!project) return;

    const companyCamRecord = project.extraBoardIntegrationsStore?.find((r: any) => r.key === "companyCam");
    if (companyCamRecord) {
      // @ts-expect-error - project.extraBoardIntegrationsStore is dynamically typed
      project.extraBoardIntegrationsStore?.remove(companyCamRecord);
      toast.success("CompanyCam access token removed successfully");
      setHasExistingToken(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* CompanyCam Integration Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold text-foreground mb-1 flex items-center gap-2">
                  CompanyCam Integration
                  {validationSuccess && hasExistingToken && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Connected
                    </span>
                  )}
                </h3>
                <p className="text-xs text-muted-foreground">Add your CompanyCam API access token to sync photos and documents</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* API Token Input */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="companycam-token" className="flex items-center gap-2">
                    <Key className="h-4 w-4 text-muted-foreground" />
                    API Access Token
                  </Label>
                  {apiToken && canEdit && (
                    <Button variant="ghost" size="sm" onClick={() => setShowToken(!showToken)} className="h-7 px-2 text-xs">
                      {showToken ? (
                        <>
                          <EyeOff className="h-3 w-3 mr-1" />
                          Hide
                        </>
                      ) : (
                        <>
                          <Eye className="h-3 w-3 mr-1" />
                          Show
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="companycam-token"
                    type={showToken ? "text" : "password"}
                    variant="background"
                    value={apiToken}
                    onChange={(e) => handleTokenChange(e.target.value)}
                    placeholder="Enter your CompanyCam API token"
                    disabled={!canEdit || isValidating}
                    className={cn(
                      "pr-10 font-mono text-sm",
                      validationError && "border-red-500 focus-visible:ring-red-500",
                      validationSuccess && hasExistingToken && "border-green-500 focus-visible:ring-green-500"
                    )}
                  />
                  {validationSuccess && hasExistingToken && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                  )}
                  {validationError && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </div>
                  )}
                </div>

                {/* Validation Error */}
                {validationError ? (
                  <IntegrationAlert
                    variant="error"
                    provider="company_cam"
                    title="CompanyCam token validation failed"
                    message={validationError}
                  />
                ) : null}

                {/* Validation Success */}
                {validationSuccess && hasExistingToken && (
                  <div className="flex items-start gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <p>Token verified and saved successfully</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {apiToken && canEdit && (
                  <Button variant="outline" onClick={handleRemoveToken} disabled={isValidating} className="min-w-[100px]">
                    Clear Token
                  </Button>
                )}
                <Button onClick={handleSaveToken} disabled={!canEdit || isValidating || !apiToken.trim()} className="min-w-[120px]">
                  {isValidating ? "Validating..." : hasExistingToken ? "Update Token" : "Save Token"}
                </Button>
              </div>

              {/* Permission Warning */}
              {!canEdit && (
                <Card className="py-3 px-4 border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/10">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Info className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        You need <strong>Administrator</strong> permissions to manage integration tokens. Demo users and sandbox boards cannot modify integrations.
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </Card>

        {/* What is CompanyCam Card */}
        <Card className="py-3 px-4">
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">About CompanyCam</h3>
              <p className="text-xs text-muted-foreground">Visual documentation for your projects</p>
            </div>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>CompanyCam is a photo documentation app that helps teams capture, organize, and share project photos. By connecting your CompanyCam account, you can:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>View CompanyCam photos directly within Swivu tasks, clients, and resources</li>
                <li>Link CompanyCam projects to your Swivu entities</li>
                <li>Access project documentation without switching apps</li>
                <li>Keep your team aligned with visual progress updates</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CompanyCamIntegrationDetail;
